import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  createSession,
  createSessionSeries,
  getClubLocations,
  type ClubLocation,
} from "../../services/api";
import DateInput from "../DateInput";

/**
 * Adaugă antrenament, pentru o echipă anume.
 *
 * The club-wide sessions screen asks which team first; opened from a team's
 * own page that question is already answered, so this form does not ask it
 * again. Everything else is the same job: one training, or a whole series of
 * them on fixed weekdays, which is how a season is actually planned.
 *
 * A series is the default because almost nobody schedules a single training.
 * The backend skips occurrences in the past, so a series started mid-week
 * creates what is still ahead and says how many that came to.
 */

const WEEKDAYS: { value: number; short: string; long: string }[] = [
  { value: 1, short: "Lu", long: "Luni" },
  { value: 2, short: "Ma", long: "Marți" },
  { value: 3, short: "Mi", long: "Miercuri" },
  { value: 4, short: "Jo", long: "Joi" },
  { value: 5, short: "Vi", long: "Vineri" },
  { value: 6, short: "Sâ", long: "Sâmbătă" },
  { value: 0, short: "Du", long: "Duminică" },
];

/** Today, as the yyyy-mm-dd the date input speaks. */
function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export type TeamSessionDialogProps = {
  teamId: string;
  teamName: string;
  clubId: string | null;
  /** The team's own staff, so the training can be attributed to one of them. */
  coaches: { id: string; full_name: string | null; email: string }[];
  /** Pre-fill the date, e.g. when the dialog is opened from a calendar day. */
  initialDate?: string;
  onClose: () => void;
  onCreated: () => void;
};

export default function TeamSessionDialog({
  teamId,
  teamName,
  clubId,
  coaches,
  initialDate,
  onClose,
  onCreated,
}: TeamSessionDialogProps) {
  const [mode, setMode] = useState<"recurring" | "one_time">("recurring");
  const [title, setTitle] = useState(`Antrenament ${teamName}`);
  const [type, setType] = useState<"sport" | "educational">("sport");
  const [coachId, setCoachId] = useState(coaches[0]?.id || "");
  const [date, setDate] = useState(initialDate || todayIso());
  const [startTime, setStartTime] = useState("17:00");
  const [endTime, setEndTime] = useState("18:30");
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [weeks, setWeeks] = useState(8);
  const [locationId, setLocationId] = useState("");
  const [locationText, setLocationText] = useState("");
  const [locations, setLocations] = useState<ClubLocation[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!clubId) return;
    getClubLocations(clubId)
      .then(setLocations)
      .catch(() => setLocations([]));
  }, [clubId]);

  // The weekday the chosen date falls on, offered as the obvious first tick so
  // a series does not start out with nothing selected.
  const dateWeekday = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay();
  }, [date]);

  useEffect(() => {
    // Only a starting point: a date change fills in the obvious weekday when
    // nothing is ticked, and leaves an existing selection alone.
    setWeekdays((prev) => (prev.length === 0 && dateWeekday !== null ? [dateWeekday] : prev));
  }, [dateWeekday]);

  const toggleWeekday = (value: number) =>
    setWeekdays((prev) => (prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]));

  const valid =
    !!title.trim() &&
    !!date &&
    !!startTime &&
    !!endTime &&
    endTime > startTime &&
    (mode === "one_time" || weekdays.length > 0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    try {
      const payload = {
        team_id: teamId,
        type,
        title: title.trim(),
        start_at: `${date}T${startTime}`,
        end_at: `${date}T${endTime}`,
        ...(coachId ? { coach_id: coachId } : {}),
        ...(locationId ? { location_id: locationId } : {}),
        ...(locationText.trim() ? { location_text: locationText.trim() } : {}),
      };

      if (mode === "one_time") {
        await createSession(payload);
        toast.success("Antrenament adăugat.");
      } else {
        const res = await createSessionSeries({ ...payload, weekdays, weeks });
        toast.success(
          res.count === 0
            ? "Nu s-a creat niciun antrenament — toate datele din interval sunt în trecut."
            : `${res.count} antrenamente adăugate.`
        );
      }
      onCreated();
      onClose();
    } catch (err: any) {
      // A booked hall comes back as 409 with the clash spelled out; show that
      // rather than a generic failure, because it is actionable.
      const data = err?.response?.data;
      toast.error(data?.message || data?.error || "Nu am putut adăuga antrenamentul.");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/50">
      <form
        onSubmit={submit}
        className="w-full max-w-lg my-8 bg-white shadow-xl rounded-2xl"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-800">Adaugă antrenament</h3>
            <p className="text-xs text-slate-500">{teamName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:bg-slate-100"
            aria-label="Închide"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* One or many */}
          <div className="flex gap-1 p-1 border rounded-xl border-slate-200 bg-slate-100">
            {([
              { key: "recurring", label: "Serie săptămânală" },
              { key: "one_time", label: "O singură dată" },
            ] as const).map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMode(m.key)}
                className={`flex-1 px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
                  mode === m.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div>
            <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="ts-title">
              Titlu
            </label>
            <input
              id="ts-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="ts-type">
                Tip
              </label>
              <select
                id="ts-type"
                value={type}
                onChange={(e) => setType(e.target.value as "sport" | "educational")}
                className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
              >
                <option value="sport">Sportiv</option>
                <option value="educational">Educațional</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="ts-coach">
                Antrenor
              </label>
              <select
                id="ts-coach"
                value={coachId}
                onChange={(e) => setCoachId(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
              >
                <option value="">Eu</option>
                {coaches.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name || c.email}
                  </option>
                ))}
              </select>
              {coaches.length === 0 && (
                <p className="mt-1 text-xs text-slate-400">
                  Echipa nu are antrenor alocat — antrenamentul rămâne pe numele tău.
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="ts-date">
                {mode === "recurring" ? "Începe de la" : "Data"}
              </label>
              <DateInput
                id="ts-date"
                mode="date"
                value={date}
                onChange={setDate}
                className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
              />
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="ts-start">
                De la
              </label>
              <input
                id="ts-start"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
              />
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="ts-end">
                Până la
              </label>
              <input
                id="ts-end"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
              />
            </div>
          </div>
          {endTime <= startTime && (
            <p className="text-xs text-rose-600">Ora de final trebuie să fie după ora de început.</p>
          )}

          {mode === "recurring" && (
            <div className="p-3 space-y-3 border rounded-xl border-slate-200 bg-slate-50">
              <div>
                <span className="block mb-1.5 text-xs font-semibold text-slate-600">Zilele săptămânii</span>
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => toggleWeekday(d.value)}
                      title={d.long}
                      className={`w-10 py-1.5 text-xs font-bold rounded-lg border transition-colors ${
                        weekdays.includes(d.value)
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"
                      }`}
                    >
                      {d.short}
                    </button>
                  ))}
                </div>
                {weekdays.length === 0 && (
                  <p className="mt-1 text-xs text-rose-600">Alege cel puțin o zi.</p>
                )}
              </div>
              <div className="w-40">
                <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="ts-weeks">
                  Câte săptămâni
                </label>
                <input
                  id="ts-weeks"
                  type="number"
                  min={1}
                  max={52}
                  value={weeks}
                  onChange={(e) => setWeeks(Math.min(52, Math.max(1, Number(e.target.value) || 1)))}
                  className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
                />
              </div>
              <p className="text-xs text-slate-500">
                Se creează {weekdays.length * weeks} antrenamente, fără cele care ar cădea în trecut.
              </p>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="ts-loc">
                Locație
              </label>
              <select
                id="ts-loc"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
              >
                <option value="">Fără locație din listă</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="ts-loctext">
                Sau scrie unde
              </label>
              <input
                id="ts-loctext"
                value={locationText}
                onChange={(e) => setLocationText(e.target.value)}
                placeholder="opțional"
                className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
              />
            </div>
          </div>
          {locationId && (
            <p className="text-xs text-slate-500">
              O locație din listă se verifică pentru suprapuneri: dacă sala e deja ocupată la ora
              aceea, antrenamentul nu se creează.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
          >
            Anulează
          </button>
          <button
            type="submit"
            disabled={!valid || saving}
            className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? "Se adaugă..." : "Adaugă"}
          </button>
        </div>
      </form>
    </div>
  );
}
